import { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import { YomiriLogo } from './YomiriLogo.tsx';
import { YomiriAccount } from '../../auth/YomiriAuth.tsx';
import { YomiriNotificationsPopover, type RecentUpdateItem } from './YomiriNotificationsPopover.tsx';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { sourceProvider } from '../../core/SourceProvider.ts';
import './header.css';

interface Props {
  onNavigate: (view: string, params?: unknown) => void;
  onOpenManga?: (id: string, chapterId?: string, manga?: any) => void;
}

export function YomiriHeader({ onNavigate, onOpenManga }: Props) {
  const [now, setNow] = useState(() => new Date());
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [updates, setUpdates] = useState<RecentUpdateItem[]>([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const fetchUpdates = useCallback(async () => {
    try {
      setLoadingUpdates(true);
      const library = (await yomiriDatabase.getAllManga()).filter((manga) => manga.inLibrary);
      if (library.length === 0) {
        setUpdates([]);
        setLoadingUpdates(false);
        return;
      }
      const next: RecentUpdateItem[] = [];
      // Inspect sources with a bounded slice to avoid freezing
      for (const manga of library.slice(0, 15)) {
        try {
          const chapters = await sourceProvider.getChapters(manga.sourceId, manga.id);
          const states = await yomiriDatabase.getAllChapterStatesForManga(manga.sourceId, manga.id);
          const unread = chapters.filter((chapter) => !(states.get(chapter.id)?.isRead ?? chapter.isRead));
          next.push(...unread.map((chapter) => ({ manga, chapter })));
        } catch {
          // Continue with next manga on source network error
        }
      }
      next.sort((a, b) => (Date.parse(b.chapter.uploadDate || '') || 0) - (Date.parse(a.chapter.uploadDate || '') || 0));
      setUpdates(next);
    } catch {
      // Ignore background fetch error
    } finally {
      setLoadingUpdates(false);
    }
  }, []);

  // Check on mount and listen to changes
  useEffect(() => {
    void fetchUpdates();
    const handleUpdate = () => { void fetchUpdates(); };
    window.addEventListener('yomiri-downloads-changed', handleUpdate);
    window.addEventListener('yomiri-sources-changed', handleUpdate);
    return () => {
      window.removeEventListener('yomiri-downloads-changed', handleUpdate);
      window.removeEventListener('yomiri-sources-changed', handleUpdate);
    };
  }, [fetchUpdates]);

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopoverAnchor(event.currentTarget);
    void fetchUpdates();
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
  };

  return (
    <Box
      component="header"
      className="y-header"
      sx={{
        display: { xs: 'none', md: 'flex' },
        height: 38,
        minHeight: '38px!important',
        px: 2,
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255, 255, 255, 0.07)',
        background: '#000000',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      <YomiriLogo size="small" animated={false} onClick={() => onNavigate('home')} />
      <div className="y-header-tools" hidden style={{display:'none'}}>
        <time className="y-header-clock" dateTime={now.toISOString()}>
          <strong>{now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })}</strong>
          <span>{now.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
        </time>
        <div className="y-header-shortcuts">
          <button
            type="button"
            aria-label="Novedades de tu biblioteca"
            title={updates.length ? `${updates.length} capítulos nuevos` : 'Novedades de tu biblioteca'}
            onClick={handleOpenPopover}
            style={{ position: 'relative' }}
          >
            <Badge
              color="error"
              variant={updates.length > 99 ? 'dot' : 'standard'}
              badgeContent={updates.length > 0 ? updates.length : 0}
              invisible={updates.length === 0}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: 10,
                  height: 16,
                  minWidth: 16,
                  px: 0.5,
                  bgcolor: 'var(--y-primary, #ff375f)',
                  color: '#ffffff',
                },
              }}
            >
              <NotificationsNoneRounded />
            </Badge>
          </button>
          <button
            type="button"
            aria-label="Abrir ajustes"
            title="Ajustes"
            onClick={() => onNavigate('more')}
          >
            <SettingsOutlined />
          </button>
        </div>
        <span id="y-header-device-link" className="y-header-device-link" />
        <YomiriAccount />
      </div>

      <YomiriNotificationsPopover
        anchorEl={popoverAnchor}
        open={Boolean(popoverAnchor)}
        onClose={handleClosePopover}
        onNavigate={onNavigate}
        onOpenManga={onOpenManga}
        items={updates}
        loading={loadingUpdates}
      />
    </Box>
  );
}
